/**
 * GA4 E-commerce Tracking Helper
 *
 * Phase B1: Google Analytics 4 + GTM Integration
 * Created: November 1, 2025
 *
 * Purpose: Centralized helper for pushing e-commerce events to GTM data layer
 *
 * Supported Events:
 * - view_item: Product page view
 * - add_to_cart: Item added to cart
 * - remove_from_cart: Item removed from cart
 * - begin_checkout: Checkout started
 * - purchase: Order completed (handled by Shopify thank you page)
 */

class GA4Ecommerce {
  constructor() {
    // Initialize data layer if not exists
    window.dataLayer = window.dataLayer || [];

    // Debug mode (same as debug-logger.js)
    this.debugEnabled = window.location.hostname === 'localhost' ||
                        window.location.hostname.includes('127.0.0.1') ||
                        window.location.hostname.includes('.myshopify.com') ||
                        window.location.search.includes('debug=true');

    this.log('GA4 Ecommerce initialized');
  }

  /**
   * Conditional logging (only in development)
   */
  log(message, ...args) {
    if (this.debugEnabled) {
      window.debugLogger.log(`[GA4] ${message}`, ...args);
    }
  }

  /**
   * Format price from cents to dollars
   */
  formatPrice(priceInCents) {
    return parseFloat((priceInCents / 100).toFixed(2));
  }

  /**
   * Track product page view
   *
   * @param {object} product - Product object from Shopify
   */
  trackViewItem(product) {
    if (!product) {
      this.log('trackViewItem: No product data provided');
      return;
    }

    const eventData = {
      event: 'view_item',
      ecommerce: {
        currency: 'USD',
        value: this.formatPrice(product.price),
        items: [{
          item_id: product.variants?.[0]?.sku || product.id,
          item_name: product.title,
          item_brand: product.vendor,
          item_category: product.type,
          price: this.formatPrice(product.price),
          quantity: 1
        }]
      }
    };

    this.log('Tracking view_item', eventData);
    window.dataLayer.push({ ecommerce: null }); // Clear previous ecommerce object
    window.dataLayer.push(eventData);
  }

  /**
   * Track add to cart event
   *
   * @param {object} item - Cart item from Shopify Cart API
   */
  trackAddToCart(item) {
    if (!item) {
      this.log('trackAddToCart: No item data provided');
      return;
    }

    const eventData = {
      event: 'add_to_cart',
      ecommerce: {
        currency: 'USD',
        value: this.formatPrice(item.price * item.quantity),
        items: [{
          item_id: item.sku || item.variant_id,
          item_name: item.product_title,
          item_brand: item.vendor,
          item_variant: item.variant_title,
          price: this.formatPrice(item.price),
          quantity: item.quantity
        }]
      }
    };

    this.log('Tracking add_to_cart', eventData);
    window.dataLayer.push({ ecommerce: null }); // Clear previous ecommerce object
    window.dataLayer.push(eventData);
  }

  /**
   * Track remove from cart event
   *
   * @param {object} item - Cart item that was removed
   */
  trackRemoveFromCart(item) {
    if (!item) {
      this.log('trackRemoveFromCart: No item data provided');
      return;
    }

    const eventData = {
      event: 'remove_from_cart',
      ecommerce: {
        currency: 'USD',
        value: this.formatPrice(item.price * item.quantity),
        items: [{
          item_id: item.sku || item.variant_id,
          item_name: item.product_title,
          item_brand: item.vendor,
          item_variant: item.variant_title,
          price: this.formatPrice(item.price),
          quantity: item.quantity
        }]
      }
    };

    this.log('Tracking remove_from_cart', eventData);
    window.dataLayer.push({ ecommerce: null }); // Clear previous ecommerce object
    window.dataLayer.push(eventData);
  }

  /**
   * Track begin checkout event
   *
   * @param {object} cart - Full cart object from Shopify
   */
  trackBeginCheckout(cart) {
    if (!cart || !cart.items) {
      this.log('trackBeginCheckout: No cart data provided');
      return;
    }

    const items = cart.items.map(item => ({
      item_id: item.sku || item.variant_id,
      item_name: item.product_title,
      item_brand: item.vendor,
      item_variant: item.variant_title,
      price: this.formatPrice(item.price),
      quantity: item.quantity
    }));

    const eventData = {
      event: 'begin_checkout',
      ecommerce: {
        currency: 'USD',
        value: this.formatPrice(cart.total_price),
        items: items
      }
    };

    this.log('Tracking begin_checkout', eventData);
    window.dataLayer.push({ ecommerce: null }); // Clear previous ecommerce object
    window.dataLayer.push(eventData);
  }

  /**
   * Track quantity change (determines if add or remove event should fire)
   *
   * @param {object} cart - Full cart object
   * @param {number} variantId - Variant ID that was updated
   * @param {number} oldQuantity - Previous quantity
   * @param {number} newQuantity - New quantity
   * @param {object} oldItem - Previous item data (needed for removals when item is no longer in cart)
   */
  trackQuantityChange(cart, variantId, oldQuantity, newQuantity, oldItem) {
    if (!cart) {
      this.log('trackQuantityChange: No cart data provided');
      return;
    }

    const quantityDiff = newQuantity - oldQuantity;

    if (quantityDiff > 0) {
      // Quantity increased - find item in updated cart
      const item = cart.items?.find(i => i.variant_id === parseInt(variantId));
      if (!item) {
        this.log('trackQuantityChange: Item not found in updated cart for add', variantId);
        return;
      }

      // Quantity increased = add_to_cart
      this.trackAddToCart({
        ...item,
        quantity: quantityDiff // Track only the added quantity
      });
    } else if (quantityDiff < 0) {
      // Quantity decreased - use oldItem data since item may no longer be in cart
      if (!oldItem) {
        this.log('trackQuantityChange: No oldItem data provided for removal', variantId);
        return;
      }

      // Quantity decreased = remove_from_cart
      this.trackRemoveFromCart({
        ...oldItem,
        quantity: Math.abs(quantityDiff) // Track only the removed quantity
      });
    }
  }
}

// Initialize global instance
window.ga4Ecommerce = new GA4Ecommerce();
